import * as https from "https";
import { inject, injectable } from "inversify";
import * as superagent from "superagent";
import { ITracker } from "../domain/interfaces/tracker.interface";
import { BOBEnvironment } from "../express/bob-environment";
import { Cache } from "./cache/cache";
import { DI } from "./di";
import { Request } from "./request";
import { Response } from "./response";

@injectable()
export class RequestDispatcher {

	constructor(
		@inject(DI.CACHE) private cache: Cache,
		@inject(DI.TRACKER) private tracker: ITracker
	) {
	}

	public async execute(request: Request): Promise<any> {

		if (BOBEnvironment.useCache() && await this.cache.has(request)) {
			const cacheResponse = await this.fetchFromCache(request);
			return cacheResponse.content;
		}

		const remoteResponse = await this.fetchFromRemote(request);

		if (BOBEnvironment.useCache()) {
			this.putResponseInCache(request, remoteResponse);
		}

		return remoteResponse.content;
	}

	private async fetchFromRemote(request: Request): Promise<Response> {

		try {

			return await this.doRequest(request);
		} catch (error) {
			this.trackFailedRequest(request, error);
			throw new Error(`Micro service request failed! ${request.url}  ${error}`);
		}
	}

	private getSSLVerificationBypassHttpsAgent() {

		return new https.Agent({
			rejectUnauthorized: false
		});
	}

	private async doRequest(request: Request): Promise<Response> {

		let response = superagent
			.get(request.url)
			.timeout({
				response: 200
			});

		if (BOBEnvironment.ignoreSSLVerification() && this.isHttps(request.url)) {

			response = response.agent(this.getSSLVerificationBypassHttpsAgent());
		}

		response = await response;
		const ttl = this.getTTL(response.header["cache-control"]);

		return new Response(response.text, {ttl});
	}

	private async fetchFromCache(request: Request): Promise<Response> {

		return await this.cache.get(request);
	}

	private getTTL(header: any): number | undefined {

		if (typeof header === "undefined") {

			return;
		}

		return Number(header.match(/\d+/g));
	}

	private putResponseInCache(request: Request, response: Response): void {

		this.cache.build(request, response);
	}

	private trackFailedRequest(request: Request, error: string) {

		this.tracker.trackEvent({
			url: request.url,
			category: "Microservice",
			action: "RequestFailed",
			label: "RequestFailed"
		});

	}

	private isHttps(url: string): boolean {

		const protocol = url.split(":")[0];

		return protocol === "https";
	}

}
